import { MenuIcon, PenIcon, TrashIcon } from "@xola/icons";
import { Badge, Table } from "@xola/ui-kit";
import clsx from "clsx";
import { SecondaryButton } from "common";
import { truncate } from "lodash";
import React from "react";
import {
    DragDropContext,
    Draggable,
    DraggableProvided,
    DraggableStateSnapshot,
    Droppable,
    DroppableProvided,
    DropResult,
} from "react-beautiful-dnd";
import { VariantGroup } from "types";

const MAX_OPTION_NAME_LENGTH = 70;

export const truncateText = (text: string) => {
    return truncate(text, {
        length: MAX_OPTION_NAME_LENGTH,
        omission: "…",
    });
};

interface VariantGroupsDndTableProps {
    variantGroups: VariantGroup[];
    onDragEnd: (result: DropResult) => void;
    onEditGroup: (group: VariantGroup, index: number) => void;
    onDeleteGroup: (group: VariantGroup, index: number) => void;
    getDisplayName: (group: VariantGroup, index: number) => string;
}

interface VariantGroupDraggableRowProps {
    group: VariantGroup;
    index: number;
    onEditGroup: (group: VariantGroup, index: number) => void;
    onDeleteGroup: (group: VariantGroup, index: number) => void;
    getDisplayName: (group: VariantGroup, index: number) => string;
}

export const VariantGroupsDndTable = ({
    variantGroups,
    onDragEnd,
    onEditGroup,
    onDeleteGroup,
    getDisplayName,
}: VariantGroupsDndTableProps) => {
    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable
                droppableId="variantGroupsTable"
                renderClone={(provided, _, rubric) => (
                    <VariantGroupCloneRow
                        provided={provided}
                        group={variantGroups[rubric.source.index]}
                        displayName={getDisplayName(variantGroups[rubric.source.index], rubric.source.index)}
                    />
                )}
            >
                {(provided: DroppableProvided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                        <Table>
                            <Table.Head>
                                <Table.Row>
                                    <Table.Header className="w-8" />
                                    <Table.Header>Name</Table.Header>
                                    <Table.Header>Variant Options</Table.Header>
                                    <Table.Header className="text-right">Actions</Table.Header>
                                </Table.Row>
                            </Table.Head>

                            <Table.Body isStriped={false}>
                                {variantGroups.map((group, index) => (
                                    <VariantGroupDraggableRow
                                        key={group.id ?? `${group.name}-${index}`}
                                        group={group}
                                        index={index}
                                        getDisplayName={getDisplayName}
                                        onEditGroup={onEditGroup}
                                        onDeleteGroup={onDeleteGroup}
                                    />
                                ))}
                                {provided.placeholder}
                            </Table.Body>
                        </Table>
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};

const VariantGroupDraggableRow = ({
    group,
    index,
    onEditGroup,
    onDeleteGroup,
    getDisplayName,
}: VariantGroupDraggableRowProps) => {
    const handleEditClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onEditGroup(group, index);
    };

    const handleDeleteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onDeleteGroup(group, index);
    };

    return (
        <Draggable draggableId={group.id ?? `${group.name}-${index}`} index={index}>
            {({ draggableProps, dragHandleProps, innerRef }, snapshot: DraggableStateSnapshot) => (
                <tr
                    ref={innerRef}
                    {...draggableProps}
                    {...dragHandleProps}
                    className={clsx("justify-between", {
                        "bg-white shadow": snapshot.isDragging,
                    })}
                >
                    <Table.Cell className="w-8">
                        <span className="inline-flex cursor-grab rounded border border-gray-light px-2 py-0.5">
                            <MenuIcon size="small" />
                        </span>
                    </Table.Cell>
                    <Table.Cell className="w-1/4" data-testid="variant-group-name">
                        <span className="whitespace-normal break-all">{getDisplayName(group, index)}</span>
                    </Table.Cell>
                    <Table.Cell>
                        <div className="flex flex-wrap gap-1">
                            {group.options.map((option, optionIndex) => {
                                const optionKey = option.id ?? `${group.name}-${optionIndex}`;

                                return (
                                    <Badge key={optionKey} className="rounded-none" color="secondary" size="small">
                                        {truncateText(option.name)}
                                    </Badge>
                                );
                            })}
                        </div>
                    </Table.Cell>
                    <Table.Cell className="w-32 space-x-1 text-right">
                        <SecondaryButton size="small" data-testid="variant-group-edit-button" onClick={handleEditClick}>
                            <PenIcon size="small" />
                        </SecondaryButton>
                        <SecondaryButton
                            size="small"
                            data-testid="variant-group-delete-button"
                            onClick={handleDeleteClick}
                        >
                            <TrashIcon size="small" />
                        </SecondaryButton>
                    </Table.Cell>
                </tr>
            )}
        </Draggable>
    );
};

interface VariantGroupCloneRowProps {
    provided: DraggableProvided;
    group: VariantGroup;
    displayName: string;
}

const VariantGroupCloneRow = ({ provided, group, displayName }: VariantGroupCloneRowProps) => (
    <table
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className="w-full bg-white shadow"
        style={provided.draggableProps.style}
    >
        <tbody>
            <tr>
                <Table.Cell className="w-12">
                    <span className="inline-flex rounded border border-gray-light px-2 py-0.5">
                        <MenuIcon size="small" />
                    </span>
                </Table.Cell>
                <Table.Cell className="w-1/4">
                    <span className="whitespace-normal break-all">{displayName}</span>
                </Table.Cell>
                <Table.Cell>
                    <div className="flex flex-wrap gap-1">
                        {group.options.map((option, optionIndex) => {
                            const optionKey = option.id ?? `${group.name}-${optionIndex}`;

                            return (
                                <Badge key={optionKey} className="rounded-none" color="secondary" size="small">
                                    {truncateText(option.name)}
                                </Badge>
                            );
                        })}
                    </div>
                </Table.Cell>
                <Table.Cell className="w-24 space-x-1 text-right">
                    <SecondaryButton size="small">
                        <PenIcon size="small" />
                    </SecondaryButton>
                    <SecondaryButton size="small">
                        <TrashIcon size="small" />
                    </SecondaryButton>
                </Table.Cell>
            </tr>
        </tbody>
    </table>
);
